import { useHomepageHydration } from "../../context/HomepageRenderContext";
import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { format, isValid, parseISO } from "date-fns";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import SmartImage from "../Image";
import { selectCartItems } from "../../store/cartSlice";
import { setSearchState } from "../../store/landingSearchSlice";
import {
  readContinueBooking,
  removeContinueBookingByKey,
} from "../../utils/continueBookingStorage";
import {
  clearRecentlyVisited,
  readRecentlyVisited,
} from "../../utils/recentlyVisited";
import {
  buildContinueBookingEntries,
  resolveContinueHref,
} from "./continueBookingEntries";

const CONTINUE_BOOKING_EVENT = "ecotour:continue-booking-updated";
const RECENTLY_VISITED_EVENT = "ecotour:recently-visited";
const CONTINUE_BOOKING_STORAGE_KEY = "ecoTourContinueBooking";
const RECENTLY_VISITED_STORAGE_KEY = "ecoTourRecentlyVisited";
const MAX_VISIBLE_RECENT_ITEMS = 4;

const formatDateLabel = (start, end, single = false) => {
  const parsedStart = start ? parseISO(String(start)) : null;
  const parsedEnd = end ? parseISO(String(end)) : null;
  if (single) {
    return parsedStart && isValid(parsedStart) ? format(parsedStart, "dd MMM") : "";
  }
  if (parsedStart && parsedEnd && isValid(parsedStart) && isValid(parsedEnd)) {
    return `${format(parsedStart, "dd MMM")} – ${format(parsedEnd, "dd MMM")}`;
  }
  return parsedStart && isValid(parsedStart) ? format(parsedStart, "dd MMM") : "";
};

const getGuestTotal = (guests = {}) => {
  const source = guests || {};
  return (
    (Number(source.adults ?? source.adult ?? 0) || 0) +
    (Number(source.children ?? source.child ?? 0) || 0) +
    (Number(source.infants ?? source.infant ?? 0) || 0)
  );
};

const buildEntrySummary = (entry = {}) => {
  const search = entry.searchState || {};
  const isExperience = search.mode === "experiences";
  const dateLabel = formatDateLabel(search.startDate, search.endDate, isExperience);
  const guestTotal = getGuestTotal(search.guests);
  const guestLabel = guestTotal ? `${guestTotal} guest${guestTotal === 1 ? "" : "s"}` : "";
  const roomLabel = !isExperience && entry.quantity
    ? `${entry.quantity} room${entry.quantity === 1 ? "" : "s"}`
    : "";
  return {
    compact: [dateLabel, guestLabel, roomLabel, entry.subtitle].filter(Boolean).join(" · "),
  };
};

const buildStoredResumeItem = (entry = {}) => {
  if (!entry.identifier) return null;
  const search = entry.searchState || {};
  const config = entry.resumeConfig || {};
  const isStay = search.mode !== "experiences";
  return {
    experienceType: isStay ? "room" : search.experienceType || "safari",
    identifier: entry.identifier,
    title: entry.title || "Booking",
    subtitle: entry.subtitle || "",
    image: entry.image || "",
    quantity: Math.max(1, Number(config.quantity || entry.quantity || 1) || 1),
    guestMix: search.guests || null,
    addOns: Array.isArray(config.addOns) ? config.addOns : [],
    selectedCharges: Array.isArray(config.selectedCharges) ? config.selectedCharges : [],
    requiredCharges: Array.isArray(config.requiredCharges) ? config.requiredCharges : [],
    metadata: {
      checkIn: search.startDate || "",
      checkOut: search.endDate || "",
      ratePlanId: config.ratePlanId || "",
      ratePlanLabel: config.ratePlanLabel || "",
      resumeDraft: true,
    },
  };
};

const EntryImage = ({ entry }) => (
  <span className="ecotour-landing-history__thumbnail">
    {entry.image ? (
      <SmartImage
        src={entry.image}
        surfaceKey="search.entity-result"
        alt=""
        fill
        sizes="40px"
        className="h-full w-full"
        loading="lazy"
      />
    ) : (
      <span aria-hidden="true">ET</span>
    )}
  </span>
);

const formatCount = (count) => (count > 9 ? "9+" : String(count));

const ContinueBookingBar = ({ isMobile = false }) => {
  const hydrating = useHomepageHydration();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const cartItems = useSelector(selectCartItems);
  const dialogTitleId = useId();
  const [storedEntries, setStoredEntries] = useState(() => readContinueBooking());
  const [recentEntries, setRecentEntries] = useState(() => readRecentlyVisited());
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerRef = useRef(null);

  const entries = useMemo(
    () => buildContinueBookingEntries(storedEntries, cartItems || []),
    [cartItems, storedEntries],
  );
  const visibleRecentEntries = recentEntries.slice(0, MAX_VISIBLE_RECENT_ITEMS);
  const totalCount = entries.length + recentEntries.length;

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const syncBookings = () => setStoredEntries(readContinueBooking());
    const syncRecent = () => setRecentEntries(readRecentlyVisited());
    const handleStorage = (event) => {
      if (event.key === CONTINUE_BOOKING_STORAGE_KEY) syncBookings();
      if (event.key === RECENTLY_VISITED_STORAGE_KEY) syncRecent();
    };
    window.addEventListener(CONTINUE_BOOKING_EVENT, syncBookings);
    window.addEventListener(RECENTLY_VISITED_EVENT, syncRecent);
    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener(CONTINUE_BOOKING_EVENT, syncBookings);
      window.removeEventListener(RECENTLY_VISITED_EVENT, syncRecent);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  useEffect(() => {
    if (!pickerOpen) return undefined;
    const handlePointerDown = (event) => {
      if (!pickerRef.current?.contains(event.target)) setPickerOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setPickerOpen(false);
    };
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [pickerOpen]);

  useEffect(() => {
    if (!totalCount) setPickerOpen(false);
  }, [totalCount]);

  if (hydrating || !totalCount) return null;

  const handleResume = (entry) => {
    if (entry.searchState) dispatch(setSearchState(entry.searchState));
    const href = resolveContinueHref(entry);
    const resumeItem = entry.cartItemKey ? null : buildStoredResumeItem(entry);
    navigate(href, {
      state: {
        continueBookingResume: {
          cartItemKey: entry.cartItemKey || "",
          identifier: entry.identifier || "",
          resumeItem,
        },
      },
    });
    setPickerOpen(false);
  };

  const handleRemoveStoredEntry = (entry) => {
    if (!entry.storageKey || entry.cartItemKey) return;
    removeContinueBookingByKey(entry.storageKey);
    setStoredEntries((current) => current.filter((item) => item.key !== entry.storageKey));
  };

  const handleVisit = (entry) => {
    navigate(entry.href || "/stays-camps");
    setPickerOpen(false);
  };

  const handleClearRecent = () => {
    clearRecentlyVisited();
    setRecentEntries([]);
  };

  const handleOpenCart = () => {
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("ecotour:open-cart", { detail: { step: 1 } }));
    }
    setPickerOpen(false);
  };

  return (
    <div
      ref={pickerRef}
      className={`ecotour-landing-history ${isMobile ? "ecotour-landing-history--mobile" : ""}`}
    >
      <button
        type="button"
        className="ecotour-landing-history__trigger"
        onClick={() => setPickerOpen((current) => !current)}
        aria-label={`Open recent activity, ${totalCount} item${totalCount === 1 ? "" : "s"}`}
        aria-haspopup="dialog"
        aria-expanded={pickerOpen}
        title="Recent activity"
      >
        <i className="fa-solid fa-clock-rotate-left" aria-hidden="true" />
        <span className="ecotour-landing-history__count" aria-hidden="true">
          {formatCount(totalCount)}
        </span>
      </button>

      {pickerOpen ? (
        <div
          className="ecotour-landing-history__popover"
          role="dialog"
          aria-modal="false"
          aria-labelledby={dialogTitleId}
        >
          <div className="ecotour-landing-history__header">
            <div>
              <p className="ecotour-landing-history__eyebrow">Your activity</p>
              <h2 id={dialogTitleId} className="ecotour-landing-history__title">Recent activity</h2>
            </div>
            <button
              type="button"
              className="ecotour-landing-history__close"
              onClick={() => setPickerOpen(false)}
              aria-label="Close recent activity"
            >
              <i className="fa-solid fa-xmark" aria-hidden="true" />
            </button>
          </div>

          <div className="ecotour-landing-history__scroll">
            {entries.length ? (
              <section className="ecotour-landing-history__section" aria-labelledby={`${dialogTitleId}-bookings`}>
                <div className="ecotour-landing-history__section-header">
                  <h3 id={`${dialogTitleId}-bookings`}>Cart & bookings</h3>
                  <span>{entries.length}</span>
                </div>
                <div className="ecotour-landing-history__list">
                  {entries.map((entry) => {
                    const summary = buildEntrySummary(entry);
                    return (
                      <div key={entry.key} className="ecotour-landing-history__item-shell">
                        <button
                          type="button"
                          className="ecotour-landing-history__item"
                          onClick={() => handleResume(entry)}
                        >
                          <EntryImage entry={entry} />
                          <span className="ecotour-landing-history__item-copy">
                            <span className="ecotour-landing-history__item-title">{entry.title}</span>
                            <span className="ecotour-landing-history__item-meta">
                              {summary.compact || (entry.cartItemKey ? "Ready in cart" : "Continue booking")}
                            </span>
                          </span>
                          {entry.cartItemKey ? (
                            <span className="ecotour-landing-history__status">In cart</span>
                          ) : (
                            <i className="fa-solid fa-arrow-right ecotour-landing-history__arrow" aria-hidden="true" />
                          )}
                        </button>
                        {entry.storageKey && !entry.cartItemKey ? (
                          <button
                            type="button"
                            className="ecotour-landing-history__remove"
                            onClick={() => handleRemoveStoredEntry(entry)}
                            aria-label={`Remove ${entry.title} from booking history`}
                          >
                            <i className="fa-regular fa-trash-can" aria-hidden="true" />
                          </button>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
                {cartItems?.length ? (
                  <button type="button" className="ecotour-landing-history__section-action" onClick={handleOpenCart}>
                    Open cart
                    <i className="fa-solid fa-arrow-right" aria-hidden="true" />
                  </button>
                ) : null}
              </section>
            ) : null}

            {recentEntries.length ? (
              <section className="ecotour-landing-history__section" aria-labelledby={`${dialogTitleId}-recent`}>
                <div className="ecotour-landing-history__section-header">
                  <h3 id={`${dialogTitleId}-recent`}>Recently viewed</h3>
                  <span>{recentEntries.length}</span>
                  <button type="button" onClick={handleClearRecent}>Clear</button>
                </div>
                <div className="ecotour-landing-history__list">
                  {visibleRecentEntries.map((entry) => (
                    <button
                      key={entry.key}
                      type="button"
                      className="ecotour-landing-history__item"
                      onClick={() => handleVisit(entry)}
                    >
                      <EntryImage entry={entry} />
                      <span className="ecotour-landing-history__item-copy">
                        <span className="ecotour-landing-history__item-title">{entry.title}</span>
                        <span className="ecotour-landing-history__item-meta">
                          {[entry.type, entry.meta || entry.subtitle].filter(Boolean).join(" · ")}
                        </span>
                      </span>
                      <i className="fa-solid fa-arrow-right ecotour-landing-history__arrow" aria-hidden="true" />
                    </button>
                  ))}
                </div>
              </section>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ContinueBookingBar;
